import React, { useEffect, useState, useCallback } from 'react';
import { crmApi } from '../api/crm';
import { creditApi } from '../api/credit';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../hooks/useToast';

const Accounts: React.FC = () => {
  const { showToast } = useToast();
  const { hasPermission } = useAuth();
  const canManage = hasPermission('crm.manage');
  const today = new Date().toISOString().split('T')[0];
  const fmt = (n:any) => Number(n||0).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2});

  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState<'all'|'due'|'owing'|'mine'>('all');
  const [search, setSearch] = useState('');
  const [people, setPeople] = useState<any[]>([]);

  const [open, setOpen] = useState<any>(null);
  const [visit, setVisit] = useState<any>(null);     // the log-contact form
  const [person, setPerson] = useState<any>(null);   // the add-person form
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    crmApi.accounts({ mine: filter === 'mine' ? 1 : undefined, search: search || undefined })
      .then(r => setRows(r.data.data || []))
      .catch(() => {}).finally(() => setLoading(false));
  }, [filter, search]);

  useEffect(() => { creditApi.salespeople().then(r => setPeople(r.data.data || [])).catch(() => {}); }, []);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const shown = rows.filter((r:any) => {
    if (filter === 'due')   return r.next_visit_date && r.next_visit_date <= today;
    if (filter === 'owing') return Number(r.ar_balance) > 0;
    return true;
  });

  const openOne = async (id:number) => {
    try { const r = await crmApi.getAccount(id); setOpen(r.data.data); }
    catch { showToast('Could not open', 'error'); }
  };

  const assignRep = async (repId:string) => {
    try {
      await crmApi.updateAccount(open.id, { assigned_to: repId ? Number(repId) : null });
      showToast('Rep set'); setOpen({ ...open, assigned_to: repId, assigned_name: people.find(p => String(p.id)===String(repId))?.name || null });
      load();
    } catch (e:any) { showToast(e.response?.data?.message || 'Could not assign', 'error'); }
  };

  const logContact = async () => {
    if (!visit.note.trim()) { showToast('Write what happened', 'error'); return; }
    setSaving(true);
    try {
      await crmApi.followUp({
        contact_id: open.id,
        action_date: visit.action_date,
        channel: visit.channel,
        note: visit.note,
        promised_amount: visit.promised_amount ? Number(visit.promised_amount) : null,
        promised_date: visit.promised_date || null,
        next_action_date: visit.next_action_date || null,
      });
      showToast('Logged'); setVisit(null); openOne(open.id); load();
    } catch (e:any) { showToast(e.response?.data?.message || 'Could not save', 'error'); }
    finally { setSaving(false); }
  };

  const savePerson = async () => {
    if (!person.name.trim()) { showToast('Name is required', 'error'); return; }
    try {
      await crmApi.addPerson({ ...person, contact_id: open.id, is_primary: !!person.is_primary });
      showToast('Person added'); setPerson(null); openOne(open.id);
    } catch (e:any) { showToast(e.response?.data?.message || 'Could not add', 'error'); }
  };

  const removePerson = async (id:number) => {
    if (!window.confirm('Remove this person?')) return;
    try { await crmApi.removePerson(id); showToast('Removed'); openOne(open.id); }
    catch { showToast('Could not remove', 'error'); }
  };

  const th: React.CSSProperties = { padding:'8px 10px', fontSize:10, fontWeight:600, color:'var(--tx3)', textTransform:'uppercase', textAlign:'left' };
  const channels = [['visit','Visit'],['call','Call'],['whatsapp','WhatsApp'],['other','Other']];

  // ---------- log contact ----------
  if (open && visit) return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Log contact — {open.name}</h1>
        <div style={{ display:'flex', gap:8 }}>
          <button className="btn btn-secondary" onClick={() => setVisit(null)}>Cancel</button>
          <button className="btn btn-primary" onClick={logContact} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
        </div>
      </div>
      <div style={{ fontSize:13, color:'var(--tx2)', marginBottom:10 }}>
        {Number(open.ar_balance) > 0 && <>Owes <span className="mono" style={{ fontWeight:700, color:'var(--red)' }}>{fmt(open.ar_balance)}</span></>}
      </div>
      <div className="card"><div className="card-body">
        <div style={{ marginBottom:10 }}>
          {channels.map(([v,l]) => (
            <button key={v} className={'btn btn-sm ' + (visit.channel===v ? 'btn-primary' : 'btn-secondary')} style={{ marginRight:6 }}
              onClick={() => setVisit({...visit, channel:v})}>{l}</button>
          ))}
        </div>
        <div className="form-group"><label className="form-label">What happened *</label>
          <textarea className="form-control" rows={2} value={visit.note} onChange={e => setVisit({...visit, note:e.target.value})} /></div>
        <div className="form-grid-2">
          <div className="form-group"><label className="form-label">Date</label>
            <input type="date" className="form-control" value={visit.action_date} onChange={e => setVisit({...visit, action_date:e.target.value})} /></div>
          <div className="form-group"><label className="form-label">Next visit / call</label>
            <input type="date" className="form-control" value={visit.next_action_date} onChange={e => setVisit({...visit, next_action_date:e.target.value})} /></div>
          <div className="form-group"><label className="form-label">Promised amount (if any)</label>
            <input className="form-control mono" style={{textAlign:'right'}} value={visit.promised_amount} onChange={e => setVisit({...visit, promised_amount:e.target.value})} /></div>
          <div className="form-group"><label className="form-label">Promised by (date)</label>
            <input type="date" className="form-control" value={visit.promised_date} onChange={e => setVisit({...visit, promised_date:e.target.value})} /></div>
        </div>
        <div style={{ fontSize:11, color:'var(--tx3)' }}>Saves to the log, sets the next visit, and a promise shows on the Credit screen too.</div>
      </div></div>
    </div>
  );

  // ---------- add person ----------
  if (open && person) return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Add person — {open.name}</h1>
        <div style={{ display:'flex', gap:8 }}>
          <button className="btn btn-secondary" onClick={() => setPerson(null)}>Cancel</button>
          <button className="btn btn-primary" onClick={savePerson}>Save</button>
        </div>
      </div>
      <div className="card"><div className="card-body">
        <div className="form-grid-2">
          <div className="form-group"><label className="form-label">Name *</label>
            <input className="form-control" value={person.name} onChange={e => setPerson({...person, name:e.target.value})} autoFocus /></div>
          <div className="form-group"><label className="form-label">Role</label>
            <input className="form-control" placeholder="Owner, Pharmacist, Buyer..." value={person.role} onChange={e => setPerson({...person, role:e.target.value})} /></div>
          <div className="form-group"><label className="form-label">Mobile</label>
            <input className="form-control mono" value={person.mobile} onChange={e => setPerson({...person, mobile:e.target.value})} /></div>
          <div className="form-group"><label className="form-label">Email</label>
            <input className="form-control" value={person.email} onChange={e => setPerson({...person, email:e.target.value})} /></div>
        </div>
        <label style={{ display:'flex', alignItems:'center', gap:8, fontSize:13 }}>
          <input type="checkbox" checked={!!person.is_primary} onChange={e => setPerson({...person, is_primary:e.target.checked})} /> Main contact
        </label>
      </div></div>
    </div>
  );

  // ---------- customer card ----------
  if (open) return (
    <div>
      <div className="page-header">
        <h1 className="page-title">{open.name}</h1>
        <div style={{ display:'flex', gap:8 }}>
          <button className="btn btn-secondary" onClick={() => { setOpen(null); load(); }}>Back</button>
          <button className="btn btn-secondary" onClick={() => setPerson({ name:'', role:'', mobile:'', email:'', is_primary:false })}>+ Person</button>
          <button className="btn btn-primary" onClick={() => setVisit({ action_date:today, channel: Number(open.ar_balance) > 0 ? 'call' : 'visit', note:'', promised_amount:'', promised_date:'', next_action_date:'' })}>Log contact</button>
        </div>
      </div>

      <div className="card"><div className="card-body">
        <div className="form-grid-2">
          <div className="form-group"><label className="form-label">Phone</label>
            <div className="form-control mono" style={{ background:'var(--sur2)' }}>{open.mobile || '—'}</div></div>
          <div className="form-group"><label className="form-label">Location</label>
            <div className="form-control" style={{ background:'var(--sur2)' }}>{open.address || '—'}</div></div>
          <div className="form-group"><label className="form-label">Owes now</label>
            <div className="form-control mono" style={{ background:'var(--sur2)', textAlign:'right', fontWeight:700, color: Number(open.ar_balance)>0 ? 'var(--red)' : 'var(--tx2)' }}>{fmt(open.ar_balance)}</div></div>
          <div className="form-group"><label className="form-label">Rep</label>
            {canManage
              ? <select className="form-control" value={open.assigned_to || ''} onChange={e => assignRep(e.target.value)}>
                  <option value="">Unassigned</option>
                  {people.map((p:any) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              : <div className="form-control" style={{ background:'var(--sur2)' }}>{open.assigned_name || 'Unassigned'}</div>}
          </div>
        </div>
      </div></div>

      <div className="card" style={{ marginTop:12 }}>
        <div className="card-header"><span>Contact people</span><span className="record-count">{(open.people||[]).length}</span></div>
        <table style={{ width:'100%', borderCollapse:'collapse' }}>
          <tbody>
            {(open.people||[]).length === 0 ? <tr><td style={{ padding:14, color:'var(--tx3)', fontSize:12 }}>Nobody recorded.</td></tr>
            : open.people.map((p:any) => (
              <tr key={p.id} style={{ borderBottom:'1px solid var(--bdr)' }}>
                <td style={{ padding:'8px 10px', fontSize:12, fontWeight:600 }}>{p.name}{p.is_primary ? <span className="badge badge-blue" style={{ marginLeft:6 }}>main</span> : null}</td>
                <td style={{ padding:'8px 10px', fontSize:12, color:'var(--tx3)' }}>{p.role || '-'}</td>
                <td style={{ padding:'8px 10px', fontSize:12 }} className="mono">{p.mobile || '-'}</td>
                <td style={{ padding:'6px 10px', textAlign:'right' }}>{canManage && <button className="btn btn-secondary btn-sm" onClick={() => removePerson(p.id)}>Remove</button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="card" style={{ marginTop:12 }}>
        <div className="card-header"><span>Contact log</span><span className="record-count">{(open.follow_ups||[]).length}</span></div>
        <table style={{ width:'100%', borderCollapse:'collapse' }}>
          <thead><tr style={{ background:'var(--sur2)' }}>
            <th style={th}>When</th><th style={th}>How</th><th style={th}>Note</th><th style={{...th, textAlign:'right'}}>Promised</th><th style={th}>By</th>
          </tr></thead>
          <tbody>
            {(open.follow_ups||[]).length === 0 ? <tr><td colSpan={5} style={{ padding:16, textAlign:'center', color:'var(--tx3)' }}>No contact logged yet.</td></tr>
            : open.follow_ups.map((f:any) => (
              <tr key={f.id} style={{ borderBottom:'1px solid var(--bdr)' }}>
                <td style={{ padding:'8px 10px', fontSize:11, color:'var(--tx3)' }}>{f.action_date}</td>
                <td style={{ padding:'8px 10px', fontSize:12 }}>{f.channel}</td>
                <td style={{ padding:'8px 10px', fontSize:12, color:'var(--tx2)' }}>{f.note}{f.next_action_date ? <span style={{ color:'var(--tx3)' }}> · next {f.next_action_date}</span> : null}</td>
                <td className="td-num" style={{ padding:'8px 10px' }}>{f.promised_amount ? fmt(f.promised_amount) : '-'}</td>
                <td style={{ padding:'8px 10px', fontSize:11, color:'var(--tx3)' }}>{f.by_name || '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  // ---------- customers list ----------
  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Customers</h1>
      </div>

      <div style={{ display:'flex', gap:6, alignItems:'center', marginBottom:12, flexWrap:'wrap' }}>
        {([['all','All'],['due','Due a visit'],['owing','Owing'],['mine','Mine']] as any).map(([v,l]:any) => (
          <button key={v} className={'btn btn-sm ' + (filter===v ? 'btn-primary' : 'btn-secondary')} onClick={() => setFilter(v)}>{l}</button>
        ))}
        <input className="form-control" style={{ width:220, marginLeft:6 }} placeholder="Search name or phone..." value={search} onChange={e => setSearch(e.target.value)} />
        <span className="record-count" style={{ marginLeft:'auto' }}>{shown.length}</span>
      </div>

      <div className="card">
        <table style={{ width:'100%', borderCollapse:'collapse' }}>
          <thead><tr style={{ background:'var(--sur2)' }}>
            <th style={th}>Customer</th><th style={th}>Phone</th><th style={th}>Last contact</th><th style={th}>Next visit</th><th style={{...th, textAlign:'right'}}>Owes</th><th style={th}>Rep</th><th></th>
          </tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={7} style={{ padding:24, textAlign:'center', color:'var(--tx3)' }}>Loading...</td></tr>
            : shown.length === 0 ? <tr><td colSpan={7} style={{ padding:24, textAlign:'center', color:'var(--tx3)' }}>No customers.</td></tr>
            : shown.map((r:any) => (
              <tr key={r.id} style={{ borderBottom:'1px solid var(--bdr)', cursor:'pointer' }} onClick={() => openOne(r.id)}>
                <td style={{ padding:'8px 10px', fontSize:12, fontWeight:600 }}>{r.name}{r.address ? <div style={{ fontSize:11, color:'var(--tx3)' }}>{r.address}</div> : null}</td>
                <td style={{ padding:'8px 10px', fontSize:12 }} className="mono">{r.mobile || '—'}</td>
                <td style={{ padding:'8px 10px', fontSize:11, color:'var(--tx3)' }}>{r.last_visit_date || 'never'}</td>
                <td style={{ padding:'8px 10px', fontSize:12, color: r.overdue ? 'var(--red)' : (r.next_visit_date && r.next_visit_date <= today ? 'var(--amber)' : 'var(--tx3)'), fontWeight: r.overdue ? 700 : 400 }}>
                  {r.next_visit_date || '—'}</td>
                <td className="td-num" style={{ padding:'8px 10px', fontWeight:600, color: Number(r.ar_balance)>0 ? 'var(--red)' : 'var(--tx3)' }}>{Number(r.ar_balance)>0 ? fmt(r.ar_balance) : '-'}</td>
                <td style={{ padding:'8px 10px', fontSize:11, color:'var(--tx3)' }}>{r.assigned_name || '—'}</td>
                <td style={{ padding:'6px 10px', textAlign:'right' }}><button className="btn btn-primary btn-sm" onClick={(e) => { e.stopPropagation(); openOne(r.id); }}>Open</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Accounts;
